import { useMemo, useState, type ReactNode } from "react";
import { usePersistentState } from "../../../hooks/usePersistentState";
import BackButton from "../../../components/navigation/BackButton";
import { useAnimals } from "../../../hooks/useAnimals";
import { useCustomers } from "../../../hooks/useCustomers";
import { usePayments } from "../../../hooks/usePayments";
import { useBankTransfers } from "../../../hooks/useBankTransfers";
import { useSharePresales } from "../../../hooks/useSharePresales";
import type { CustomerItem } from "../../../mocks/customers";
import type { AnimalItem, ShareItem } from "../../../mocks/animals";
import type { PaymentRecord } from "../../../mocks/payments";
import { getAnimalSalesType, getDisplayKupeNo, getDisplaySiparisNo } from "../../../lib/animalLabels";
import { buildGroupedHisseNo, createGroupedSale, getShareSlotNos, type ExtendedShare } from "../../../lib/salesFlow";
import { getReportPrintSettings } from "../../../lib/reportPrintSettings";
import { buildSalePrintPayload, openSalePrintWindow } from "../../../lib/printTemplates";
import { getShareTotal, recalcAnimalTotals, recalcCustomersFromAnimals } from "../../../lib/cascadeOps";
import SatisEkleDrawer, { type SaleFormData } from "../musteriler/components/SatisEkleDrawer";
import HisseSatisEkleDrawer from "../hisse-satislari/components/HisseSatisEkleDrawer";
import MoneyInput from "../../../components/form/MoneyInput";
import { formatTry, parseMoneyInput } from "../../../lib/money";
import HeaderFilterCell, { FilterFooter, FilterOptionList, FilterRangeInputs, FilterSearchInput, FilterSortControls } from "../../../components/table/HeaderFilterCell";
import {
  compareTextForSort,
  compareNumberForSort,
  applySortDirection,
  matchesContains,
  matchesExact,
  matchesNumericRange,
  type NumericRangeFilter,
  type SortDirection,
} from "../../../lib/tableFilters";
import { formatDateTr, matchesOptionalDateRange } from "../../../lib/dateFilters";
import PhoneActionMenu from "../../../components/contact/PhoneActionMenu";
import { applyOperationResult } from "../../../lib/operationRefresh";
import { createSaleOperationRequest } from "../../../lib/api";

function fmt(value: number) {
  return value.toLocaleString("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 });
}

function norm(value: string) {
  return value.trim().toLocaleLowerCase("tr-TR");
}

type SaleSortKey =
  | "tarih"
  | "fisNo"
  | "kupeNo"
  | "siparisNo"
  | "tur"
  | "satisTipi"
  | "musteri"
  | "kesimTarihi"
  | "note"
  | "toplam"
  | "odenen"
  | "kalan";

type SaleListItem = {
  id: string;
  animalId: number;
  shareId: number;
  tarih: string;
  kesimTarihi: string;
  fisNo: string;
  kupeNo: string;
  siparisNo: string;
  satisTipi: string;
  tur: string;
  musteri: string;
  musteriId?: number;
  telefon: string;
  toplam: number;
  odenen: number;
  kalan: number;
  note: string;
  hisseNo: string;
  source?: "animal" | "presale";
};

type SaleFilters = {
  general: string;
  dateFrom: string;
  dateTo: string;
  kesimDateFrom: string;
  kesimDateTo: string;
  fisNo: string;
  kupeNo: string;
  siparisNo: string;
  satisTipi: string;
  tur: string;
  musteri: string;
  note: string;
  noteStatus: "Hepsi" | "Var" | "Yok";
  toplam: NumericRangeFilter;
  odenen: NumericRangeFilter;
  kalan: NumericRangeFilter;
};

const EMPTY_SALE_FILTERS: SaleFilters = {
  general: "",
  dateFrom: "",
  dateTo: "",
  kesimDateFrom: "",
  kesimDateTo: "",
  fisNo: "",
  kupeNo: "",
  siparisNo: "",
  satisTipi: "",
  tur: "",
  musteri: "",
  note: "",
  noteStatus: "Hepsi",
  toplam: { min: "", max: "" },
  odenen: { min: "", max: "" },
  kalan: { min: "", max: "" },
};

const SALE_SORT_LABELS: Record<SaleSortKey, string> = {
  tarih: "Tarih",
  fisNo: "Fiş No",
  kupeNo: "Küpe",
  siparisNo: "Sipariş",
  satisTipi: "Satış Tipi",
  tur: "Büyükbaş/Küçükbaş",
  musteri: "Müşteri",
  kesimTarihi: "Kesim Tarihi",
  note: "Satış Notu",
  toplam: "Toplam",
  odenen: "Ödenen",
  kalan: "Kalan",
};

function compareSalesByKey(a: SaleListItem, b: SaleListItem, key: SaleSortKey) {
  if (key === "toplam") return compareNumberForSort(a.toplam, b.toplam);
  if (key === "odenen") return compareNumberForSort(a.odenen, b.odenen);
  if (key === "kalan") return compareNumberForSort(a.kalan, b.kalan);
  return compareTextForSort(String(a[key] ?? ""), String(b[key] ?? ""));
}

function hasFilters(filters: SaleFilters) {
  return JSON.stringify(filters) !== JSON.stringify(EMPTY_SALE_FILTERS);
}

function buildNextPaymentId(existing: PaymentRecord[]) {
  const maxId = existing.reduce((max, item) => Math.max(max, item.id), 0);
  const candidate = Date.now() * 1000 + Math.floor(Math.random() * 1000);
  return Math.max(maxId + 1, candidate);
}

type SaleEditPayload = {
  updates: Partial<ShareItem>;
  kaporaTutar: number;
  kaporaTip: "Nakit" | "Havale";
  kaporaDekontNo: string;
  kaporaAciklama: string;
};

function SaleEditModal({
  sale,
  animals,
  payments,
  onClose,
  onSave,
}: {
  sale: SaleListItem;
  animals: AnimalItem[];
  payments: PaymentRecord[];
  onClose: () => void;
  onSave: (payload: SaleEditPayload) => void;
}) {
  const animal = animals.find((item) => item.id === sale.animalId) ?? null;
  const share = animal?.hisseler.find((item) => item.id === sale.shareId) ?? null;
  const sharePayments = payments.filter((payment) => payment.shareId === sale.shareId).sort((a, b) => a.tarih.localeCompare(b.tarih) || a.id - b.id);
  const kaporaPayment = sharePayments[0] ?? null;

  const initialSlots = share ? getShareSlotNos(share) : [];
  const [selectedSlots, setSelectedSlots] = useState<number[]>(initialSlots);
  const [form, setForm] = useState({
    tutar: String(Math.max(0, share?.tutar ?? 0)),
    kesimUcreti: String(Math.max(0, share?.kesimUcreti ?? 0)),
    parcalamaUcreti: String(Math.max(0, share?.parcalamaUcreti ?? 0)),
    tasimaUcreti: String(Math.max(0, share?.tasimaUcreti ?? 0)),
    tasimaVar: Boolean(share?.tasimaVar ?? (share?.tasimaUcreti ?? 0) > 0),
    indirim: String(Math.max(0, share?.indirim ?? 0)),
    satisNotu: share?.satisNotu ?? "",
    satisFisNo: share?.satisFisNo ?? "",
    kaporaTutar: String(Math.max(0, kaporaPayment?.tutar ?? 0)),
    kaporaTip: (kaporaPayment?.tip ?? "Nakit") as "Nakit" | "Havale",
    kaporaDekontNo: kaporaPayment?.havaleNo ?? share?.pesinatDekontNo ?? "",
    kaporaAciklama: kaporaPayment?.aciklama ?? share?.pesinatAciklama ?? "",
  });

  if (!animal || !share) return null;

  const usedByOthers = new Set<number>();
  for (const item of animal.hisseler ?? []) {
    if (item.id === share.id) continue;
    for (const slot of getShareSlotNos(item)) usedByOthers.add(slot);
  }

  const selectableSlots: number[] = [];
  for (let i = 1; i <= animal.toplamHisse; i++) {
    if (!usedByOthers.has(i)) selectableSlots.push(i);
  }

  function toggleSlot(slotNo: number) {
    setSelectedSlots((prev) =>
      prev.includes(slotNo)
        ? prev.filter((item) => item !== slotNo)
        : [...prev, slotNo].sort((a, b) => a - b)
    );
  }

  const toplam =
    parseMoneyInput(form.tutar) +
    parseMoneyInput(form.kesimUcreti) +
    parseMoneyInput(form.parcalamaUcreti) +
    parseMoneyInput(form.tasimaUcreti) -
    parseMoneyInput(form.indirim);
  const yeniKapora = Math.max(0, parseMoneyInput(form.kaporaTutar));
  const oncekiKapora = Math.max(0, kaporaPayment?.tutar ?? 0);
  const digerOdemeler = Math.max(0, share.odenen - oncekiKapora);
  const yeniOdenen = digerOdemeler + yeniKapora;
  const bakiye = toplam - yeniOdenen;

  function save() {
    if (selectedSlots.length === 0) return;
    if (toplam < 0) return;
    if (form.kaporaTip === "Havale" && yeniKapora > 0 && !form.kaporaDekontNo.trim()) return;

    const sortedSlots = [...selectedSlots].sort((a, b) => a - b);
    onSave({
      updates: {
        slotNos: sortedSlots,
        hisseNo: buildGroupedHisseNo(animal.siraNo, sortedSlots),
        hisseSayisi: sortedSlots.length,
        tutar: parseMoneyInput(form.tutar),
        kesimUcreti: parseMoneyInput(form.kesimUcreti),
        parcalamaUcreti: parseMoneyInput(form.parcalamaUcreti),
        tasimaUcreti: parseMoneyInput(form.tasimaUcreti),
        tasimaVar: form.tasimaVar,
        indirim: parseMoneyInput(form.indirim),
        satisNotu: form.satisNotu.trim() || undefined,
        satisFisNo: form.satisFisNo.trim() || undefined,
        pesinatAciklama: form.kaporaAciklama.trim() || undefined,
        pesinatDekontNo: form.kaporaDekontNo.trim() || undefined,
      } as Partial<ShareItem>,
      kaporaTutar: yeniKapora,
      kaporaTip: form.kaporaTip,
      kaporaDekontNo: form.kaporaDekontNo.trim(),
      kaporaAciklama: form.kaporaAciklama.trim(),
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 px-0 sm:items-center sm:px-4">
      <div className="w-full max-w-2xl rounded-t-farm border border-farm-border bg-farm-warm shadow-xl sm:rounded-farm">
        <div className="flex items-center justify-between border-b border-farm-border px-4 py-3">
          <div>
            <p className="font-heading text-sm font-bold text-farm-brown">Satışı Düzenle — {share.hisseNo}</p>
            <p className="text-[11px] text-farm-muted">Hisse ekleyebilir veya çıkarabilirsin. Boşalan hisse hayvanda tekrar kullanılabilir olur.</p>
          </div>
          <button type="button" onClick={onClose} className="h-8 w-8 rounded-full text-farm-muted hover:bg-farm-cream"><i className="ri-close-line" /></button>
        </div>

        <div className="max-h-[78vh] space-y-3 overflow-y-auto px-4 py-4">
          <div className="rounded-farm border border-farm-border bg-white/70 px-3 py-3">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-farm-muted">Hisse Seçimi</p>
            <div className="flex flex-wrap gap-2">
              {selectableSlots.map((slotNo) => {
                const active = selectedSlots.includes(slotNo);
                return (
                  <button key={slotNo} type="button" onClick={() => toggleSlot(slotNo)} className={active ? "rounded-full border border-farm-earth bg-farm-earth px-3 py-1.5 text-xs font-semibold text-white" : "rounded-full border border-farm-border bg-farm-cream px-3 py-1.5 text-xs font-semibold text-farm-brown"}>
                    Hisse {slotNo}
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-[11px] text-farm-muted">Seçili: <span className="font-semibold text-farm-brown">{selectedSlots.join(", ") || "—"}</span></p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <InputBox label="Toplam Satış Tutarı"><MoneyInput formatWhileTyping value={form.tutar} onChange={(v) => setForm((p) => ({ ...p, tutar: v }))} /></InputBox>
            <InputBox label="Fiş No"><input value={form.satisFisNo} onChange={(e) => setForm((p) => ({ ...p, satisFisNo: e.target.value }))} className="h-10 w-full rounded-farm border border-farm-border bg-farm-cream px-3 text-xs text-farm-brown outline-none" /></InputBox>
            <InputBox label="Toplam Kesim"><MoneyInput formatWhileTyping value={form.kesimUcreti} onChange={(v) => setForm((p) => ({ ...p, kesimUcreti: v }))} /></InputBox>
            <InputBox label="Toplam Parçalama"><MoneyInput formatWhileTyping value={form.parcalamaUcreti} onChange={(v) => setForm((p) => ({ ...p, parcalamaUcreti: v }))} /></InputBox>
            <InputBox label="Toplam Taşıma"><MoneyInput formatWhileTyping value={form.tasimaUcreti} onChange={(v) => setForm((p) => ({ ...p, tasimaUcreti: v }))} /></InputBox>
            <InputBox label="Toplam İndirim"><MoneyInput formatWhileTyping value={form.indirim} onChange={(v) => setForm((p) => ({ ...p, indirim: v }))} /></InputBox>
          </div>

          <label className="flex items-center gap-2 rounded-farm border border-farm-border bg-farm-cream px-3 py-2 text-xs font-semibold text-farm-brown">
            <input type="checkbox" checked={form.tasimaVar} onChange={(e) => setForm((p) => ({ ...p, tasimaVar: e.target.checked }))} /> Taşıma Var
          </label>

          <div className="rounded-farm border border-farm-border bg-white/70 px-3 py-3 space-y-3">
            <p className="text-xs font-bold text-farm-brown">Kapora</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <InputBox label="Kapora Tutarı"><MoneyInput formatWhileTyping value={form.kaporaTutar} onChange={(v) => setForm((p) => ({ ...p, kaporaTutar: v }))} /></InputBox>
              <InputBox label="Kapora Tipi"><div className="grid grid-cols-2 gap-2">{(["Nakit", "Havale"] as const).map((item) => <button key={item} type="button" onClick={() => setForm((p) => ({ ...p, kaporaTip: item }))} className={form.kaporaTip === item ? "h-10 rounded-farm border border-farm-earth bg-farm-earth text-xs font-semibold text-white" : "h-10 rounded-farm border border-farm-border bg-farm-cream text-xs font-semibold text-farm-brown"}>{item}</button>)}</div></InputBox>
              <InputBox label="Havale / Dekont No"><input value={form.kaporaDekontNo} onChange={(e) => setForm((p) => ({ ...p, kaporaDekontNo: e.target.value }))} className="h-10 w-full rounded-farm border border-farm-border bg-farm-cream px-3 text-xs text-farm-brown outline-none" /></InputBox>
              <InputBox label="Kapora Açıklama"><input value={form.kaporaAciklama} onChange={(e) => setForm((p) => ({ ...p, kaporaAciklama: e.target.value }))} className="h-10 w-full rounded-farm border border-farm-border bg-farm-cream px-3 text-xs text-farm-brown outline-none" /></InputBox>
            </div>
          </div>

          <InputBox label="Satış Notu"><textarea value={form.satisNotu} onChange={(e) => setForm((p) => ({ ...p, satisNotu: e.target.value }))} rows={3} className="w-full rounded-farm border border-farm-border bg-farm-cream px-3 py-2 text-xs text-farm-brown outline-none" /></InputBox>

          <div className="rounded-farm border border-farm-border bg-farm-cream px-3 py-3 space-y-1.5">
            <SummaryLine label="Kapora + Diğer Ödemeler" value={formatTry(yeniOdenen)} />
            <SummaryLine label="Yeni Genel Toplam" value={formatTry(toplam)} strong />
            <SummaryLine label="Kalan / Bakiye" value={formatTry(bakiye)} strong />
          </div>
        </div>

        <div className="flex gap-2 border-t border-farm-border bg-white px-4 py-3">
          <button type="button" onClick={onClose} className="h-10 flex-1 rounded-full border border-farm-border text-xs font-semibold text-farm-muted">İptal</button>
          <button type="button" onClick={save} disabled={selectedSlots.length === 0 || toplam < 0 || (form.kaporaTip === "Havale" && yeniKapora > 0 && !form.kaporaDekontNo.trim())} className="h-10 flex-1 rounded-full bg-farm-earth text-xs font-semibold text-white disabled:opacity-50">Kaydet</button>
        </div>
      </div>
    </div>
  );
}

function InputBox({ label, children }: { label: string; children: ReactNode }) {
  return <div><label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-farm-muted">{label}</label>{children}</div>;
}

function SummaryLine({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return <div className="flex items-center justify-between text-xs"><span className="text-farm-muted">{label}</span><span className={strong ? "font-bold text-farm-earth" : "font-semibold text-farm-brown"}>{value}</span></div>;
}

export default function SatislarPage() {
  const { animals, setAllAnimals } = useAnimals();
  const { customers, setAllCustomers } = useCustomers();
  const { payments, setAllPayments } = usePayments();
  const { setAllBankTransfers } = useBankTransfers();
  const { sharePresales } = useSharePresales();

  const [filters, setFilters] = usePersistentState<SaleFilters>("ec.filters.satislar.v3", EMPTY_SALE_FILTERS);
  const [sortKey, setSortKey] = usePersistentState<SaleSortKey>("ec.sort.satislar.key.v2", "tarih");
  const [sortDir, setSortDir] = usePersistentState<SortDirection>("ec.sort.satislar.dir.v1", "desc");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [customerQuery, setCustomerQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerItem | null>(null);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [presaleDrawerOpen, setPresaleDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<SaleListItem | null>(null);
  const [presaleEditTargetId, setPresaleEditTargetId] = useState<number | null>(null);

  const sales = useMemo<SaleListItem[]>(() => {
    const animalSales = animals.flatMap((animal) =>
      (animal.hisseler ?? [])
        .filter((share) => String(share.musteriAdi ?? "").trim() && !(share as ExtendedShare & { sourcePresaleSaleNo?: string }).sourcePresaleSaleNo)
        .map((share) => {
          const ext = share as ExtendedShare;
          return {
            id: `${animal.id}-${share.id}`,
            animalId: animal.id,
            shareId: share.id,
            tarih: ext.satisTarihi ?? "",
            kesimTarihi: animal.kesimGunu ?? "",
            fisNo: ext.satisFisNo ?? "",
            kupeNo: getDisplayKupeNo(animal),
            siparisNo: getDisplaySiparisNo(animal),
            satisTipi: getAnimalSalesType(animal),
            tur: animal.tur ?? "Büyükbaş",
            musteri: share.musteriAdi,
            musteriId: share.musteriId,
            telefon: share.telefon ?? "",
            toplam: getShareTotal(share),
            odenen: share.odenen,
            kalan: share.kalan,
            note: ext.satisNotu ?? "",
            hisseNo: share.hisseNo,
            source: "animal" as const,
          };
        })
    );

    const presaleSales = sharePresales.sales
      .filter((sale) => sale.durum !== "İptal")
      .map((sale) => ({
        id: `presale-${sale.id}`,
        animalId: 0,
        shareId: 0,
        tarih: sale.tarih ?? "",
        kesimTarihi: "",
        fisNo: sale.havaleNo ?? "",
        kupeNo: sale.linkedKupeNo ?? "",
        siparisNo: sale.linkedSiparisNo ?? "",
        satisTipi: "Hisseli",
        tur: "Büyükbaş",
        musteri: sale.musteriAdi,
        musteriId: sale.musteriId,
        telefon: sale.telefon,
        toplam: sale.toplamTutar,
        odenen: sale.kaporaTutar,
        kalan: sale.kalanTutar,
        note: sale.not ?? "",
        hisseNo: sale.linkedHisseNo ?? sale.satisNo,
        source: "presale" as const,
      }));

    return [...animalSales, ...presaleSales];
  }, [animals, sharePresales.sales]);

  const filteredSales = useMemo(() => {
    const general = norm(filters.general);
    const rows = sales.filter((sale) => {
      if (general) {
        const haystack = [sale.tarih, formatDateTr(sale.tarih), sale.kesimTarihi, formatDateTr(sale.kesimTarihi), sale.fisNo, sale.kupeNo, sale.siparisNo, sale.tur, sale.satisTipi, sale.musteri, sale.telefon, sale.hisseNo, sale.note, sale.toplam, sale.odenen, sale.kalan].join(" ").toLocaleLowerCase("tr-TR");
        if (!haystack.includes(general)) return false;
      }

      if (!matchesOptionalDateRange(sale.tarih, filters.dateFrom, filters.dateTo)) return false;
      if (!matchesOptionalDateRange(sale.kesimTarihi, filters.kesimDateFrom, filters.kesimDateTo)) return false;
      if (!matchesExact(sale.fisNo, filters.fisNo)) return false;
      if (!matchesExact(sale.kupeNo, filters.kupeNo)) return false;
      if (!matchesExact(sale.siparisNo, filters.siparisNo)) return false;
      if ((filters.tur ?? "") && sale.tur !== filters.tur) return false;
      if (filters.satisTipi) {
        if (filters.satisTipi === "Hisseli" && sale.satisTipi !== "Hisseli") return false;
        if (filters.satisTipi === "Hissesiz" && sale.satisTipi !== "Hissesiz") return false;
      }
      if (!matchesContains(`${sale.musteri} ${sale.telefon}`, filters.musteri)) return false;
      if (!matchesContains(sale.note, filters.note)) return false;
      const hasSaleNote = Boolean(String(sale.note ?? "").trim());
      if (filters.noteStatus === "Var" && !hasSaleNote) return false;
      if (filters.noteStatus === "Yok" && hasSaleNote) return false;
      if (!matchesNumericRange(sale.toplam, filters.toplam)) return false;
      if (!matchesNumericRange(sale.odenen, filters.odenen)) return false;
      if (!matchesNumericRange(sale.kalan, filters.kalan)) return false;
      return true;
    });

    return rows.sort((a, b) => applySortDirection(compareSalesByKey(a, b, sortKey), sortDir));
  }, [filters, sales, sortDir, sortKey]);

  const filteredCustomers = useMemo(() => {
    const q = norm(customerQuery);
    if (!q) return customers.slice(0, 20);
    return customers.filter((customer) => `${customer.adSoyad} ${customer.telefon}`.toLocaleLowerCase("tr-TR").includes(q)).slice(0, 40);
  }, [customerQuery, customers]);

  const presaleEditTarget = useMemo(
    () => sharePresales.sales.find((sale) => sale.id === presaleEditTargetId) ?? null,
    [presaleEditTargetId, sharePresales.sales]
  );

  function setFilter<K extends keyof SaleFilters>(key: K, value: SaleFilters[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function renderSortControls(key: SaleSortKey, ascLabel?: string, descLabel?: string) {
    return (
      <FilterSortControls
        activeDirection={sortKey === key ? sortDir : null}
        ascLabel={ascLabel}
        descLabel={descLabel}
        onSort={(direction) => {
          setSortKey(key);
          setSortDir(direction);
        }}
      />
    );
  }

  async function handleSave(data: SaleFormData) {
    const customer = customers.find((item) => item.id === data.customerId);
    if (!customer) return;

    try {
      const result = await createSaleOperationRequest(data as unknown as Record<string, unknown>);
      await applyOperationResult(result);

      const printSettings = getReportPrintSettings();
      if (printSettings.saleAutoPrint) {
        const animal = (result.animals as any[]).find((item) => item.id === data.animalId);
        if (animal) {
          openSalePrintWindow(buildSalePrintPayload({
            animal,
            customerName: customer.adSoyad,
            hisseLabel: data.hisseNo,
            saleAmount: data.birimSatisTutar,
            kesimUcreti: data.kesimUcreti,
            parcalamaUcreti: data.parcalamaUcreti,
            tasimaUcreti: data.tasimaUcreti,
            grandTotal: data.toplam,
            depositAmount: data.pesinatTutar,
            remainingAmount: data.toplam - data.pesinatTutar,
            operationalNote: data.kisaNot,
            fisNo: data.fisNo,
            tarih: data.tarih,
          }), printSettings);
        }
      }

      setSelectedCustomer(null);
    } catch (error) {
      console.error(error);
      window.alert("Satış kaydı sırasında çakışma veya bağlantı hatası oluştu. Lütfen tekrar deneyin.");
    }
  }

  function handleEditSave(sale: SaleListItem, payload: SaleEditPayload) {
    const { updates, kaporaTutar, kaporaTip, kaporaDekontNo, kaporaAciklama } = payload;
    const ownerAnimal = animals.find((animal) => animal.id === sale.animalId);
    const currentShare = ownerAnimal?.hisseler.find((share) => share.id === sale.shareId);
    if (!ownerAnimal || !currentShare) return;

    const customer = customers.find((item) => item.id === currentShare.musteriId) ?? customers.find((item) => item.adSoyad === currentShare.musteriAdi);
    if (!customer) return;

    const relatedPayments = payments.filter((payment) => payment.shareId === sale.shareId).sort((a, b) => a.tarih.localeCompare(b.tarih) || a.id - b.id);
    const kaporaPayment = relatedPayments[0] ?? null;
    const otherPayments = kaporaPayment ? relatedPayments.filter((payment) => payment.id !== kaporaPayment.id) : relatedPayments;
    const otherPaymentsTotal = otherPayments.reduce((sum, payment) => sum + (payment.tutar || 0), 0);
    const safeKapora = Math.max(0, kaporaTutar || 0);
    const safeKaporaAciklama = kaporaAciklama.trim();
    let nextPayments = [...payments];

    const nextHisseNo = String(updates.hisseNo ?? currentShare.hisseNo);

    if (kaporaPayment) {
      if (safeKapora > 0) {
        nextPayments = nextPayments.map((payment) =>
          payment.id === kaporaPayment.id
            ? { ...payment, tutar: safeKapora, tip: kaporaTip, aciklama: safeKaporaAciklama, havaleNo: kaporaDekontNo || undefined, fisNo: updates.satisFisNo?.trim() || payment.fisNo, hisseNo: nextHisseNo }
            : payment.shareId === sale.shareId
            ? { ...payment, fisNo: updates.satisFisNo?.trim() || payment.fisNo, hisseNo: nextHisseNo }
            : payment
        );
      } else {
        nextPayments = nextPayments.filter((payment) => payment.id !== kaporaPayment.id).map((payment) => payment.shareId === sale.shareId ? { ...payment, fisNo: updates.satisFisNo?.trim() || payment.fisNo, hisseNo: nextHisseNo } : payment);
      }
    } else if (safeKapora > 0) {
      nextPayments = [
        { id: buildNextPaymentId(payments), tarih: currentShare.satisTarihi || new Date().toISOString().slice(0, 10), musteriAdi: customer.adSoyad, telefon: customer.telefon, musteriId: customer.id, hayvanKupeNo: ownerAnimal.kupeNo, hayvanSiparisNo: String(ownerAnimal.siraNo ?? ""), animalId: ownerAnimal.id, hisseNo: nextHisseNo, shareId: sale.shareId, tutar: safeKapora, tip: kaporaTip, aciklama: safeKaporaAciklama, havaleNo: kaporaDekontNo || undefined, fisNo: updates.satisFisNo?.trim() || currentShare.satisFisNo || undefined, olusturan: "Admin" } as PaymentRecord,
        ...nextPayments.map((payment) => payment.shareId === sale.shareId ? { ...payment, fisNo: updates.satisFisNo?.trim() || payment.fisNo, hisseNo: nextHisseNo } : payment),
      ];
    } else {
      nextPayments = nextPayments.map((payment) => payment.shareId === sale.shareId ? { ...payment, fisNo: updates.satisFisNo?.trim() || payment.fisNo, hisseNo: nextHisseNo } : payment);
    }

    const nextOdenen = otherPaymentsTotal + safeKapora;
    const nextAnimals = animals.map((animal) => {
      if (animal.id !== sale.animalId) return animal;
      const nextAnimal = {
        ...animal,
        hisseler: animal.hisseler.map((share) => share.id === sale.shareId ? { ...share, ...updates, pesinatAciklama: kaporaAciklama.trim() || undefined, pesinatDekontNo: kaporaDekontNo.trim() || undefined, odenen: nextOdenen } as ShareItem : share),
      };
      return recalcAnimalTotals(nextAnimal);
    });

    const nextCustomers = recalcCustomersFromAnimals(customers, nextAnimals);
    void setAllPayments(() => nextPayments);
    void setAllAnimals(() => nextAnimals);
    void setAllCustomers(() => nextCustomers);
  }

  function handlePrintSale(sale: SaleListItem) {
    if (sale.source === "presale") {
      const win = window.open("", "_blank", "width=760,height=900");
      if (!win) return;
      win.document.write(`<!doctype html><html><head><title>Hisse Ön Satış</title><style>body{font-family:Arial,sans-serif;margin:24px;color:#3f2a1f}.card{border:1px solid #d9c8b6;border-radius:14px;padding:18px}.row{display:flex;justify-content:space-between;border-bottom:1px solid #eee;padding:8px 0}h1{font-size:22px;margin:0 0 8px}.muted{color:#7a6a5d;font-size:12px}@media print{button{display:none}}</style></head><body><div class="card"><h1>Evren Çiftliği - Hisse Ön Satış</h1><div class="row"><b>Tarih</b><span>${formatDateTr(sale.tarih)}</span></div><div class="row"><b>Müşteri</b><span>${sale.musteri}</span></div><div class="row"><b>Telefon</b><span>${sale.telefon}</span></div><div class="row"><b>Satış No</b><span>${sale.hisseNo}</span></div><div class="row"><b>Küpe / Sipariş</b><span>${sale.kupeNo || "Bağlanmadı"} ${sale.siparisNo || ""}</span></div><div class="row"><b>Toplam</b><span>${fmt(sale.toplam)}</span></div><div class="row"><b>Kapora</b><span>${fmt(sale.odenen)}</span></div><div class="row"><b>Kalan</b><span>${fmt(sale.kalan)}</span></div>${sale.note ? `<p class="muted"><b>Not:</b> ${sale.note}</p>` : ""}<button onclick="window.print()">Yazdır</button></div></body></html>`);
      win.document.close();
      return;
    }

    const animal = animals.find((item) => item.id === sale.animalId);
    const share = animal?.hisseler.find((item) => item.id === sale.shareId);
    if (!animal || !share) return;
    const firstPayment = payments.filter((payment) => payment.shareId === share.id).sort((a, b) => a.tarih.localeCompare(b.tarih) || a.id - b.id)[0];
    openSalePrintWindow(buildSalePrintPayload({
      animal,
      customerName: share.musteriAdi,
      hisseLabel: share.hisseNo,
      saleAmount: share.tutar,
      kesimUcreti: share.kesimUcreti ?? 0,
      parcalamaUcreti: share.parcalamaUcreti ?? 0,
      tasimaUcreti: share.tasimaUcreti ?? 0,
      grandTotal: getShareTotal(share),
      depositAmount: firstPayment?.tutar ?? 0,
      remainingAmount: share.kalan,
      operationalNote: share.satisNotu,
      fisNo: share.satisFisNo,
      tarih: share.satisTarihi ?? sale.tarih,
    }));
  }

  const activeFilters = hasFilters(filters);

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="max-w-[1550px] mx-auto px-3 sm:px-5 py-4 space-y-4">
        <div className="lg:sticky lg:top-0 z-40 -mx-3 border-b border-farm-border/70 bg-farm-cream/95 px-3 pb-3 pt-1 shadow-sm backdrop-blur sm:-mx-5 sm:px-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-heading text-xl font-bold text-farm-brown">Satışlar</h1>
              <p className="mt-1 text-sm text-farm-muted">Tüm satış kayıtları ödeme sayfası mantığında tek listede görünür.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <BackButton />
              {activeFilters ? <button type="button" onClick={() => setFilters(EMPTY_SALE_FILTERS)} className="h-10 rounded-full border border-farm-border bg-white px-4 text-sm font-semibold text-farm-muted hover:bg-farm-cream">Filtreleri Temizle</button> : null}
              <button type="button" onClick={() => setMobileFiltersOpen((prev) => !prev)} className="lg:hidden h-10 rounded-full border border-farm-border bg-white px-4 text-sm font-semibold text-farm-brown hover:bg-farm-cream"><i className="ri-filter-3-line mr-1" /> Mobil Filtre</button>
              <button type="button" onClick={() => setChooserOpen(true)} className="h-10 rounded-full bg-farm-earth px-4 text-sm font-semibold text-white hover:bg-farm-brown"><i className="ri-add-line mr-1" /> Hayvan Satışı Ekle</button>
              <button type="button" onClick={() => setPresaleDrawerOpen(true)} className="h-10 rounded-full border border-farm-earth bg-white px-4 text-sm font-semibold text-farm-earth hover:bg-farm-cream"><i className="ri-add-line mr-1" /> Hisse Satışı Ekle</button>
            </div>
          </div>

          {mobileFiltersOpen && (
            <div className="lg:hidden mt-3 rounded-farm border border-farm-border bg-white p-3 space-y-3">
              <FilterSearchInput value={filters.general} onChange={(value) => setFilter("general", value)} placeholder="Genel ara: fiş, küpe, müşteri, not" />
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input type="date" value={filters.dateFrom} onChange={(e) => setFilter("dateFrom", e.target.value)} className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm" />
                <input type="date" value={filters.dateTo} onChange={(e) => setFilter("dateTo", e.target.value)} className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm" />
                <FilterSearchInput value={filters.fisNo} onChange={(value) => setFilter("fisNo", value)} placeholder="Fiş No ara" />
                <FilterSearchInput value={filters.kupeNo} onChange={(value) => setFilter("kupeNo", value)} placeholder="Küpe No ara" />
                <FilterSearchInput value={filters.siparisNo} onChange={(value) => setFilter("siparisNo", value)} placeholder="Sipariş No ara" />
                <FilterSearchInput value={filters.musteri} onChange={(value) => setFilter("musteri", value)} placeholder="Müşteri / telefon" />
                <FilterSearchInput value={filters.note} onChange={(value) => setFilter("note", value)} placeholder="Satış notu" />
                <select value={filters.noteStatus} onChange={(e) => setFilter("noteStatus", e.target.value as "Hepsi" | "Var" | "Yok")} className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth">
                  <option value="Hepsi">Satış Notu: Hepsi</option>
                  <option value="Var">Satış Notu: Var</option>
                  <option value="Yok">Satış Notu: Yok</option>
                </select>
              </div>
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <select value={sortKey} onChange={(e) => setSortKey(e.target.value as SaleSortKey)} className="h-10 rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth">
                  {(Object.keys(SALE_SORT_LABELS) as SaleSortKey[]).map((key) => <option key={key} value={key}>Sırala: {SALE_SORT_LABELS[key]}</option>)}
                </select>
                <button type="button" onClick={() => setSortDir((prev) => (prev === "asc" ? "desc" : "asc"))} className="h-10 rounded-xl border border-farm-border bg-white px-3 text-sm font-semibold text-farm-brown">{sortDir === "asc" ? "Artan" : "Azalan"}</button>
              </div>
            </div>
          )}

          <div className="mt-3 grid grid-cols-1 gap-3 rounded-farm border border-farm-border bg-white p-3 md:grid-cols-3">
            <input value={filters.kupeNo} onChange={(event) => setFilter("kupeNo", event.target.value)} placeholder="Küpe No ara" className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none focus:border-farm-earth" />
            <input value={filters.siparisNo} onChange={(event) => setFilter("siparisNo", event.target.value)} placeholder="Sipariş No ara" className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none focus:border-farm-earth" />
            <input value={filters.musteri} onChange={(event) => setFilter("musteri", event.target.value)} placeholder="Müşteri ara" className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none focus:border-farm-earth" />
          </div>

          <div className="mt-3 overflow-visible rounded-t-farm border border-farm-border border-b-0 bg-farm-warm">
            <div className="hidden min-w-[1450px] bg-farm-cream lg:grid grid-cols-[76px_78px_72px_82px_86px_84px_1.18fr_105px_130px_100px_100px_100px_90px] text-[10px] font-semibold uppercase tracking-wide text-farm-muted shadow-sm">
              <HeaderFilterCell label="Tarih" active={Boolean(filters.dateFrom || filters.dateTo) || sortKey === "tarih"}>{renderSortControls("tarih", "Eskiden yeniye", "Yeniden eskiye")}<div className="space-y-2"><input type="date" value={filters.dateFrom} onChange={(event) => setFilter("dateFrom", event.target.value)} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth" /><input type="date" value={filters.dateTo} onChange={(event) => setFilter("dateTo", event.target.value)} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth" /></div><FilterFooter onClear={() => setFilters((prev) => ({ ...prev, dateFrom: "", dateTo: "" }))} hint="Aralık" /></HeaderFilterCell>
              <HeaderFilterCell label="Fiş No" active={Boolean(filters.fisNo) || sortKey === "fisNo"}>{renderSortControls("fisNo")}<FilterSearchInput value={filters.fisNo} onChange={(value) => setFilter("fisNo", value)} placeholder="Fiş No ara" /><FilterFooter onClear={() => setFilter("fisNo", "")} hint="Tam eşleşme" /></HeaderFilterCell>
              <HeaderFilterCell label="Küpe" active={Boolean(filters.kupeNo) || sortKey === "kupeNo"}>{renderSortControls("kupeNo")}<FilterSearchInput value={filters.kupeNo} onChange={(value) => setFilter("kupeNo", value)} placeholder="Küpe ara" /><FilterFooter onClear={() => setFilter("kupeNo", "")} hint="Tam eşleşme" /></HeaderFilterCell>
              <HeaderFilterCell label="Sipariş" active={Boolean(filters.siparisNo) || sortKey === "siparisNo"}>{renderSortControls("siparisNo")}<FilterSearchInput value={filters.siparisNo} onChange={(value) => setFilter("siparisNo", value)} placeholder="Sipariş ara" /><FilterFooter onClear={() => setFilter("siparisNo", "")} hint="Tam eşleşme" /></HeaderFilterCell>
              <HeaderFilterCell label="Satış Tipi" active={Boolean(filters.satisTipi) || sortKey === "satisTipi"}>{renderSortControls("satisTipi")}<FilterOptionList options={["Hisseli", "Hissesiz"]} selected={filters.satisTipi ? [filters.satisTipi] : []} onToggle={(value) => setFilter("satisTipi", filters.satisTipi === value ? "" : value)} /><FilterFooter onClear={() => setFilter("satisTipi", "")} hint="Hisseli / Hissesiz" /></HeaderFilterCell><HeaderFilterCell label="Büyük/Küçük" active={Boolean(filters.tur) || sortKey === "tur"}>{renderSortControls("tur")}<FilterOptionList options={["Büyükbaş", "Küçükbaş"]} selected={(filters.tur ?? "") ? [filters.tur] : []} onToggle={(value) => setFilter("tur", filters.tur === value ? "" : value)} /><FilterFooter onClear={() => setFilter("tur", "")} hint="Büyükbaş / Küçükbaş" /></HeaderFilterCell>
              <HeaderFilterCell label="Müşteri" active={Boolean(filters.musteri) || sortKey === "musteri"}>{renderSortControls("musteri")}<FilterSearchInput value={filters.musteri} onChange={(value) => setFilter("musteri", value)} placeholder="Müşteri / telefon" /><FilterFooter onClear={() => setFilter("musteri", "")} /></HeaderFilterCell>
              <HeaderFilterCell label="Kesim Tarihi" active={Boolean(filters.kesimDateFrom || filters.kesimDateTo) || sortKey === "kesimTarihi"}>{renderSortControls("kesimTarihi", "Eskiden yeniye", "Yeniden eskiye")}<div className="space-y-2"><input type="date" value={filters.kesimDateFrom} onChange={(event) => setFilter("kesimDateFrom", event.target.value)} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth" /><input type="date" value={filters.kesimDateTo} onChange={(event) => setFilter("kesimDateTo", event.target.value)} className="h-10 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth" /></div><FilterFooter onClear={() => setFilters((prev) => ({ ...prev, kesimDateFrom: "", kesimDateTo: "" }))} hint="Aralık" /></HeaderFilterCell>
              <HeaderFilterCell label="Satış Notu" active={Boolean(filters.note) || filters.noteStatus !== "Hepsi" || sortKey === "note"}>{renderSortControls("note")}<FilterOptionList options={["Var", "Yok"]} selected={filters.noteStatus === "Hepsi" ? [] : [filters.noteStatus]} onToggle={(value) => setFilter("noteStatus", filters.noteStatus === value ? "Hepsi" : (value as "Var" | "Yok"))} /><div className="mt-2"><FilterSearchInput value={filters.note} onChange={(value) => setFilter("note", value)} placeholder="Not ara" /></div><FilterFooter onClear={() => { setFilter("note", ""); setFilter("noteStatus", "Hepsi"); }} hint="Hepsi / Var / Yok" /></HeaderFilterCell>
              <HeaderFilterCell label="Toplam" active={Boolean(filters.toplam.min || filters.toplam.max) || sortKey === "toplam"} align="right">{renderSortControls("toplam", "Küçükten büyüğe", "Büyükten küçüğe")}<FilterRangeInputs min={filters.toplam.min} max={filters.toplam.max} onMinChange={(value) => setFilter("toplam", { ...filters.toplam, min: value })} onMaxChange={(value) => setFilter("toplam", { ...filters.toplam, max: value })} /><FilterFooter onClear={() => setFilter("toplam", { min: "", max: "" })} /></HeaderFilterCell>
              <HeaderFilterCell label="Ödenen" active={Boolean(filters.odenen.min || filters.odenen.max) || sortKey === "odenen"} align="right">{renderSortControls("odenen", "Küçükten büyüğe", "Büyükten küçüğe")}<FilterRangeInputs min={filters.odenen.min} max={filters.odenen.max} onMinChange={(value) => setFilter("odenen", { ...filters.odenen, min: value })} onMaxChange={(value) => setFilter("odenen", { ...filters.odenen, max: value })} /><FilterFooter onClear={() => setFilter("odenen", { min: "", max: "" })} /></HeaderFilterCell>
              <HeaderFilterCell label="Kalan" active={Boolean(filters.kalan.min || filters.kalan.max) || sortKey === "kalan"} align="right">{renderSortControls("kalan", "Küçükten büyüğe", "Büyükten küçüğe")}<FilterRangeInputs min={filters.kalan.min} max={filters.kalan.max} onMinChange={(value) => setFilter("kalan", { ...filters.kalan, min: value })} onMaxChange={(value) => setFilter("kalan", { ...filters.kalan, max: value })} /><FilterFooter onClear={() => setFilter("kalan", { min: "", max: "" })} /></HeaderFilterCell>
              <div className="px-2 py-2 text-right">İşlem</div>
            </div>
          </div>
        </div>

        <div className="overflow-visible rounded-b-farm border border-farm-border border-t-0 bg-farm-warm">
          {filteredSales.length === 0 ? <div className="px-4 py-10 text-center text-sm text-farm-muted">Satış kaydı bulunamadı.</div> : filteredSales.map((sale) => (
            <div key={sale.id} className="border-t border-farm-border px-3 py-2.5 text-xs lg:grid lg:min-w-[1450px] lg:grid-cols-[76px_78px_72px_82px_86px_84px_1.18fr_105px_130px_100px_100px_100px_90px] lg:items-center lg:gap-0">
              <div className="font-semibold text-farm-brown">{formatDateTr(sale.tarih)}</div>
              <div>{sale.fisNo || "—"}</div>
              <div>{sale.kupeNo || "—"}</div>
              <div>{sale.siparisNo || "—"}</div>
              <div>{sale.satisTipi}</div>
              <div>{sale.tur}</div>
              <div className="min-w-0"><div className="truncate font-semibold text-farm-brown">{sale.musteri}</div><div className="text-[10px] text-farm-muted">{sale.hisseNo} • <PhoneActionMenu phone={sale.telefon} buttonClassName="font-semibold text-farm-earth hover:text-farm-brown" /></div></div>
              <div className="font-semibold text-farm-muted">{formatDateTr(sale.kesimTarihi)}</div>
              <div className="min-w-0 text-farm-muted"><span className="line-clamp-2" title={sale.note}>{sale.note || "—"}</span></div>
              <div className="text-right font-semibold text-farm-brown">{fmt(sale.toplam)}</div>
              <div className="text-right font-semibold text-farm-green">{fmt(sale.odenen)}</div>
              <div className={`text-right font-semibold ${sale.kalan > 0 ? "text-farm-earth" : "text-farm-muted"}`}>{fmt(sale.kalan)}</div>
              <div className="flex justify-end gap-1 whitespace-nowrap">
                <button
                  type="button"
                  onClick={() => {
                    if (sale.source === "presale") {
                      const id = Number(String(sale.id).replace("presale-", ""));
                      setPresaleEditTargetId(Number.isFinite(id) ? id : null);
                    } else {
                      setEditTarget(sale);
                    }
                  }}
                  className="h-8 w-8 rounded-full border border-farm-border text-farm-earth hover:bg-white"
                  title="Düzenle"
                >
                  <i className="ri-edit-line" />
                </button>
                <button type="button" onClick={() => handlePrintSale(sale)} className="h-8 w-8 rounded-full border border-farm-border text-farm-brown hover:bg-white" title="Yazdır"><i className="ri-printer-line" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {chooserOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-3">
          <div className="w-full max-w-xl rounded-farm bg-white p-4 shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-farm-border pb-3"><div><h2 className="font-heading text-lg font-bold text-farm-brown">Müşteri Seç</h2><p className="text-xs text-farm-muted">Satış yapacağınız müşteriyi seçin.</p></div><button type="button" onClick={() => setChooserOpen(false)} className="h-8 w-8 rounded-full text-farm-muted hover:bg-farm-cream"><i className="ri-close-line" /></button></div>
            <input value={customerQuery} onChange={(e) => setCustomerQuery(e.target.value)} placeholder="Müşteri adı veya telefon" className="mt-3 h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm outline-none focus:border-farm-earth" />
            <div className="mt-3 max-h-[360px] divide-y divide-farm-border overflow-y-auto rounded-2xl border border-farm-border">{filteredCustomers.map((customer) => <button key={customer.id} type="button" onClick={() => { setSelectedCustomer(customer); setChooserOpen(false); }} className="block w-full px-3 py-2.5 text-left hover:bg-farm-cream"><div className="text-sm font-semibold text-farm-brown">{customer.adSoyad}</div><div className="text-xs text-farm-muted">{customer.telefon}</div></button>)}</div>
          </div>
        </div>
      )}

      {selectedCustomer && <SatisEkleDrawer customer={selectedCustomer} onClose={() => setSelectedCustomer(null)} onSave={handleSave} />}
      {presaleDrawerOpen && <HisseSatisEkleDrawer onClose={() => setPresaleDrawerOpen(false)} />}
      {presaleEditTarget && <HisseSatisEkleDrawer editingSale={presaleEditTarget} onClose={() => setPresaleEditTargetId(null)} />}
      {editTarget && <SaleEditModal sale={editTarget} animals={animals} payments={payments} onClose={() => setEditTarget(null)} onSave={(payload) => handleEditSave(editTarget, payload)} />}
    </div>
  );
}
